/**
 * The recepten module's icon set — small stroke SVGs in the app's house style
 * (`viewBox 0 0 24 24`, `currentColor`, round joins), so nothing on these
 * screens is an emoji standing in for an icon. They replaced the 📌/📖/🕑/🍳
 * colour glyphs the OS drew, which read as clip-art beside the plain type.
 *
 * A deliberately separate copy from the todo/weather sets — a module never
 * imports another module's internals (the independence rule) — kept tiny.
 *
 * All are decorative: each sits inside a control or beside real text that
 * carries the meaning, so they are `aria-hidden`.
 */

const base = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** The pin-to-top marker — a pushpin, drawn head-on. */
export function PinIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="1.8" {...base} aria-hidden>
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
      <path d="M12 17v5" />
    </svg>
  );
}

/** The total-time marker beside a recipe's minutes. */
export function ClockIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="1.8" {...base} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.4 2" />
    </svg>
  );
}

/** The source marker beside a cookbook / site name. */
export function BookIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="1.8" {...base} aria-hidden>
      <path d="M5 4.6A1.6 1.6 0 0 1 6.6 3H19a1 1 0 0 1 1 1v13.8a1 1 0 0 1-1 1H6.6A1.6 1.6 0 0 0 5 20.4Z" />
      <path d="M5 17.4A1.6 1.6 0 0 1 6.6 16H20" />
    </svg>
  );
}

/** The cook-count marker — a pot with two wisps of steam. */
export function PotIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="1.8" {...base} aria-hidden>
      <path d="M4.5 10h15v4a4 4 0 0 1-4 4H8.5a4 4 0 0 1-4-4Z" />
      <path d="M3 10h18" />
      <path d="M9 6.5c0-1 .9-1.4 1.4-.9M14 6.5c0-1 .9-1.4 1.4-.9" />
    </svg>
  );
}

/** Beside "Naar boodschappenlijst" — a shopping basket, the groceries seam. */
export function BasketIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="1.8" {...base} aria-hidden>
      <path d="M4.6 9h14.8l-1.2 8.3a2 2 0 0 1-2 1.7H7.8a2 2 0 0 1-2-1.7Z" />
      <path d="M3 9h18" />
      <path d="M8.6 9a3.4 3.4 0 0 1 6.8 0" />
    </svg>
  );
}
